<script lang="ts">
import { ETitleFontSizes } from '../../../config/enums.js';
import BlockHeader from '../BlockHeader/BlockHeader.svelte';

interface Props {
    header?: string;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    titleSize?: string;
    ctaTo?: string;
    ctaText?: string;
    noAlternatingColumns?: boolean;
    itemsPerRow?: number;
    hasBorder?: boolean;
    hasBottomMargin?: boolean;
    children?: import('svelte').Snippet;
    hideBlock?: boolean;
}

let {
    header = '',
    headingLevel = 'h2',
    titleSize = ETitleFontSizes.H2,
    ctaTo = '',
    ctaText = '',
    noAlternatingColumns = false,
    itemsPerRow = 2,
    hasBorder = true,
    hasBottomMargin = true,
    hideBlock = false,
    children,
}: Props = $props();

let itemsPerRowTransformed = $derived(Array(itemsPerRow).fill('1fr').join(' '));
</script>

{#if !hideBlock}
    <div class="card-grid-block">
        <BlockHeader {header} {headingLevel} {titleSize} {ctaTo} {ctaText} />
        <div
            class="card-grid"
            class:card-grid--no-alternating-columns={noAlternatingColumns}
            class:card-grid--alternating-columns={!noAlternatingColumns}
            class:card-grid--has-border={hasBorder}
            class:card-grid--has-bottommargin={hasBottomMargin}
            style="--items-per-row: {itemsPerRowTransformed}"
        >
            {@render children?.()}
        </div>
    </div>
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/mq-config';
    @use 'sass-mq/mq' as *;

    .card-grid-block {
        padding-left: var(--layout-pagegrid-margin);
        padding-right: var(--layout-pagegrid-margin);
    }

    .card-grid {
        display: grid !important;
        gap: calc(var(--layout-pagegrid-gutter) * 2) var(--layout-pagegrid-gutter);
        grid-template-columns: [first] repeat(var(--layout-pagegrid-col-count), 1fr) [end];
        object-fit: fill;
    }

    .card-grid--no-alternating-columns :global(.builder-block) {
        grid-column-start: 1;
        grid-column-end: end;

        @include mq($from: desktop) {
            grid-column-start: unset;
            grid-column-end: unset;
        }
    }
    .card-grid--alternating-columns :global(.builder-block) {
        grid-column-start: 1;
        grid-column-end: end;
    }

    @include mq($from: desktop) {
        .card-grid--no-alternating-columns {
            grid-template-columns: var(--items-per-row);
        }

        /* first, 6th, 11th, 16th, 21st, 26th, 31st, 36th, 41st, 46th, 51st, 56th, 61st, 66th, 71st, 76th, 81st, 86th, 91st, 96th */
        .card-grid--alternating-columns :global(.builder-block:nth-of-type(5n + 1)) {
            grid-column-start: 1;
            grid-column-end: 7;
        }

        /* 2nd, 7th, 12th, 17th, 22nd, 27th, 32nd, 37th, 42nd, 47th, 52nd, 57th, 62nd, 67th, 72nd, 77th, 82nd, 87th, 92nd, 97th */
        .card-grid--alternating-columns :global(.builder-block:nth-of-type(5n + 2)) {
            grid-column-start: 7;
            grid-column-end: end;
        }

        /* 3rd, 8th, 13th, 18th, 23rd, 28th, 33rd, 38th, 43rd, 48th, 53rd, 58th, 63rd, 68th, 73rd, 78th, 83rd, 88th, 93rd, 98th */
        .card-grid--alternating-columns :global(.builder-block:nth-of-type(5n + 3)) {
            grid-column-start: 1;
            grid-column-end: 5;
        }

        /* 4th, 9th, 14th, 19th, 24th, 29th, 34th, 39th, 44th, 49th, 54th, 59th, 64th, 69th, 74th, 79th, 84th, 89th, 94th, 99th */
        .card-grid--alternating-columns :global(.builder-block:nth-of-type(5n + 4)) {
            grid-column-start: 5;
            grid-column-end: 9;
        }

        /* 5th, 10th, 15th, 20th, 25th, 30th, 35th, 40th, 45th, 50th, 55th, 60th, 65th, 70th, 75th, 80th, 85th, 90th, 95th, 100th */
        .card-grid--alternating-columns :global(.builder-block:nth-of-type(5n + 5)) {
            grid-column-start: 9;
            grid-column-end: end;
        }
    }

    .card-grid :global(img) {
        aspect-ratio: var(--layout-media-ar);
        object-fit: cover;
    }
</style>
